import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportInvitationTypes } from './api/invitationTypeApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { invitationTypeColumns } from './components/InvitationTypeColumns'
import InvitationTypeFormDialog from './components/InvitationTypeFormDialog'
import { useInvitationTypes } from './hooks/useInvitationTypes'
import type { InvitationType, InvitationTypeUpsert } from './types'
import ReorderableLookupCards from '../../components/ui/ReorderableLookupCards'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

/**
 * Etkinlik Takip → Davet Türü yönetim ekranı (025). Liste (paylaşılan ui/DataTable) + ekle/düzenle
 * (FormDialog) + durum toggle + soft-delete (confirmDialog) + tablo üzeri ↑/↓ sıralama. Başlık = menü adı
 * (`nav.etkinlik.invitationTypes`). (023 EventTypesPage deseniyle aynı; korumalı sistem kaydı yok.)
 */
export default function InvitationTypesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, create, update, changeStatus, remove, reorder } = useInvitationTypes()
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<InvitationType | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  const submitting = create.isPending || update.isPending
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: InvitationType) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('activity.invitationTypes.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: InvitationTypeUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('activity.invitationTypes.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('activity.invitationTypes.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: InvitationType, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('activity.invitationTypes.toast.statusChanged')),
        // Hata (optimistic-lock/generic) → merkezî toast (019).
      },
    )
  }

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip tüm id sırasını backend'e yaz (1..N).
  const onMove = (row: InvitationType, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    reorder.mutate(orderedIds, {
      onError: () => notify('error', t('common.reorder.error')),
    })
  }

  const onDelete = (row: InvitationType) => {
    confirmDialog({
      header: t('activity.invitationTypes.delete.title'),
      message: t('activity.invitationTypes.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('activity.invitationTypes.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('activity.invitationTypes.toast.deleted')),
          // Hata → merkezî toast (019).
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <InvitationTypeFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.etkinlik.invitationTypes')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportInvitationTypes} disabled={rows.length === 0} />
            <Button
              icon="pi pi-plus"
              label={t('activity.invitationTypes.actions.new')}
              onClick={openCreate}
            />
          </>
        }
      />

      {/* Sıralanabilir (SORT_INDEX) liste → sayfalama kapalı, kolon sıralaması yok; daima sıra düzeninde. */}
      {isMobile ? (
        <ReorderableLookupCards
          items={rows}
          loading={list.isLoading}
          busyId={busyId}
          reordering={reorder.isPending}
          onMove={onMove}
          onEdit={openEdit}
          onToggle={onToggle}
          onDelete={onDelete}
          emptyTitle={t('activity.invitationTypes.empty.title')}
          emptyDescription={t('activity.invitationTypes.empty.desc')}
          labels={{
            active: t('activity.invitationTypes.status.active'),
            passive: t('activity.invitationTypes.status.passive'),
            edit: t('activity.invitationTypes.actions.edit'),
            delete: t('activity.invitationTypes.actions.delete'),
            toggle: t('activity.invitationTypes.actions.toggle'),
            moveUp: t('common.reorder.up'),
            moveDown: t('common.reorder.down'),
            menu: t('common.actionsMenu'),
          }}
        />
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        paginator={false}
        emptyMessage={
          <EmptyState
            title={t('activity.invitationTypes.empty.title')}
            description={t('activity.invitationTypes.empty.desc')}
          />
        }
      >
        {invitationTypeColumns({
          t,
          localized,
          busyId,
          rowCount: rows.length,
          reordering: reorder.isPending,
          onMove,
          onEdit: openEdit,
          onToggle,
          onDelete,
        })}
      </DataTable>
      )}
    </div>
  )
}
